import clsx from 'clsx';
import {
  Activity,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';

import {
  IconEye,
  IconTrash,
  IconXboxX,
  IconBook2,
  IconSearch,
  IconSchool,
  IconMoodKid,
  IconLockOpen,
  IconMoodCheck,
  IconAdjustments,
} from '@tabler/icons-react';

import ActionDropdown from '@/components/ActionDropdown';
import { AvatarGroupList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import Input from '@/components/Input/Input';
import { SummaryCard } from '@/components/SummaryCard';
import CustomTable from '@/components/Table/index';
import { CommonStatusConfig } from '@/constants/constant';
import { ECommonStatus } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import {
  getChildrenSchoolSubscriberList,
  getChildrenSchoolSubscriberSummary,
} from '@/service/childrenService';
import { getSchoolDropdown } from '@/service/commonService';
import useCommonStore from '@/store/useCommonStore';
import {
  IStatsDataItem,
  ISelectedRowValue,
  ISubscriptionChildDetails,
  IChildrenSchoolFilterValues,
} from '@/types';
import {
  pickBy,
  getAssetUrl,
  customPredicate,
  formatLastActive,
  getStartOfMonthUnix,
} from '@/utils';

import ChildrenFilterModal from './modal/ChildrenFilterModal';

const SUMMARY_CONFIG = [
  {
    key: 'totalStudent',
    icon: <IconMoodKid color="#717784" />,
    label: 'children.school-subscriber.summary-cards.total-students',
  },
  {
    key: 'totalSchool',
    icon: <IconSchool color="#717784" />,
    label: 'children.school-subscriber.summary-cards.total-schools',
  },
  {
    key: 'totalTeacher',
    icon: <IconBook2 color="#717784" />,
    label: 'children.school-subscriber.summary-cards.total-teachers',
  },
  {
    key: 'activeStudent',
    icon: <IconMoodCheck color="#717784" />,
    label: 'children.school-subscriber.summary-cards.active-students',
  },
];

const SchoolSubscriber = forwardRef(
  (
    {
      setSelectedChildren,
      setIsOpenBlockModal,
      setIsRemoveChildrenModalOpen,
      handleBlockChildren,
      setIsChildDetailsModalOpen,
    }: {
      setSelectedChildren: (value: ISelectedRowValue | null) => void;
      setIsOpenBlockModal: (value: boolean) => void;
      setIsRemoveChildrenModalOpen: (value: boolean) => void;
      handleBlockChildren: (children: ISubscriptionChildDetails) => void;
      setIsChildDetailsModalOpen: (value: boolean) => void;
    },
    ref
  ) => {
    const { setTitle, setIsBreadcrumb } = usePageTitle();
    const { formatText } = useFormatText();
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search?.trim(), 500);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [sortField, setSortField] = useState<string>('');
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');
    const [childrens, setChildrens] = useState<ISubscriptionChildDetails[]>([]);
    const [statsData, setStatsData] = useState<IStatsDataItem[]>(
      SUMMARY_CONFIG.map((item) => ({
        ...item,
        value: '0',
        trend: '0%',
        trendLabel: 'common.this-month',
        trendDirection: 'STABLE',
      }))
    );
    const { loadingTable, setLoadingTable, loadingSummary, setLoadingSummary } =
      useCommonStore();
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const pageTitle = formatText('children.title');
    const userManagement = formatText('user-management.title');

    const [schoolOptions, setSchoolOptions] = useState<
      { value: string; label: string }[]
    >([]);

    const [filterValues, setFilterValues] =
      useState<IChildrenSchoolFilterValues>({
        status: undefined,
        school_id: undefined,
        class_id: undefined,
        teacher_id: undefined,
      });

    const handleFilterApply = (filters: IChildrenSchoolFilterValues) => {
      setFilterValues(filters);
    };

    useEffect(() => {
      const fetchSchools = async () => {
        const response = await getSchoolDropdown();
        if (response?.status) {
          const options =
            response?.data?.map((item: { id: string; name: string }) => ({
              value: item?.id,
              label: item?.name,
            })) || [];
          setSchoolOptions(options);
        }
      };
      fetchSchools();
    }, []);

    useEffect(() => {
      setCurrentPage(1);
    }, [debouncedSearch]);

    useEffect(() => {
      document.title = pageTitle;
      setTitle(pageTitle);
      setIsBreadcrumb([{ label: userManagement }, { label: pageTitle }]);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageTitle, userManagement]);

    const fetchSchoolChildrens = async () => {
      setLoadingTable(true);
      const params = pickBy(
        {
          search: debouncedSearch || undefined,
          offset: (currentPage - 1) * pageSize,
          limit: pageSize,
          order: sortField ? { [sortField]: sortDirection } : undefined,
          status: filterValues?.status || undefined,
          school_id:
            Array.isArray(filterValues?.school_id) &&
            filterValues?.school_id.length > 0
              ? filterValues?.school_id
              : undefined,
          class_id:
            Array.isArray(filterValues?.class_id) &&
            filterValues?.class_id.length > 0
              ? filterValues?.class_id
              : undefined,
          teacher_id:
            Array.isArray(filterValues?.teacher_id) &&
            filterValues?.teacher_id.length > 0
              ? filterValues?.teacher_id
              : undefined,
        },
        customPredicate
      );
      const response = await getChildrenSchoolSubscriberList(params);

      if (response?.status) {
        // Use API data if available, otherwise use dummy data for testing
        const apiData = response?.data || [];

        if (apiData?.length > 0) {
          setChildrens(apiData);
          setTotal(response?.total || 0);
        } else {
          setChildrens([]);
          setTotal(0);
        }
      } else {
        setChildrens([]);
        setTotal(0);
      }
      setLoadingTable(false);
    };

    const fetchSummary = async () => {
      setLoadingSummary(true);
      const monthTimestamp = getStartOfMonthUnix();

      const response = await getChildrenSchoolSubscriberSummary({
        month: monthTimestamp,
      });
      if (response?.status) {
        const apiData = response?.data;
        const mappedSummary = SUMMARY_CONFIG?.map((item) => {
          const itemData = apiData[item?.key as keyof typeof apiData];
          return {
            ...item,
            value: itemData?.total?.toLocaleString() || '0',
            trend: (itemData?.growth || 0) + '%',
            trendLabel: 'common.this-month',
            trendDirection: itemData?.trend || 'STABLE',
          };
        });
        setStatsData(mappedSummary);
      }
      setLoadingSummary(false);
    };

    useEffect(() => {
      fetchSummary();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      fetchSchoolChildrens();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      debouncedSearch,
      currentPage,
      pageSize,
      sortField,
      sortDirection,
      filterValues,
    ]);

    useImperativeHandle(ref, () => ({
      fetchSchoolChildrens,
      fetchSummary,
    }));

    const handleSort = (key: string) => {
      if (sortField === key) {
        setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
      } else {
        setSortField(key);
        setSortDirection('ASC');
      }
    };

    const columns = [
      {
        title: 'children.school-subscriber.columns.name', // key updated
        key: 'name',
        width: '20%',
        sorter: true,
        handleSort: () => handleSort('name'),
        render: (row: ISubscriptionChildDetails) =>
          row?.name ? (
            <div className="flex items-center gap-2">
              <AvatarGroupList
                list={[
                  {
                    id: row.id,
                    name: row.name,
                    image: getAssetUrl(row?.asset),
                  },
                ]}
                size={32}
                avatarClassName="w-8 h-8 text-[10px]"
              />
              <span className="text-sm font-medium text-neutral-primary">
                {row?.name}
              </span>
            </div>
          ) : (
            '-'
          ),
      },
      {
        title: 'children.school-subscriber.columns.email',
        key: 'email',
        width: '220px',
        sorter: true,
        handleSort: () => handleSort('email'),
        render: (row: ISubscriptionChildDetails) => (
          <span
            className="text-sm text-neutral-primary block truncate max-w-50"
            title={row?.email}
          >
            {row?.email || '-'}
          </span>
        ),
      },
      {
        title: 'children.school-subscriber.columns.school',
        key: 'school_name',
        width: '25%',
        sorter: true,
        handleSort: () => handleSort('school_name'),
        render: (row: ISubscriptionChildDetails) => (
          <div className="flex flex-col">
            <span className="text-sm font-medium text-neutral-primary">
              {row?.school?.name || '-'}
            </span>
          </div>
        ),
      },
      {
        title: 'children.school-subscriber.columns.assigned-class',
        key: 'class_name',
        width: '25%',
        sorter: false,
        handleSort: () => handleSort('class_name'),
        render: (row: ISubscriptionChildDetails) => (
          <div className="flex flex-col">
            <span className="text-sm font-medium text-neutral-primary">
              {row?.class?.name || '-'}
            </span>
          </div>
        ),
      },
      {
        title: 'children.school-subscriber.columns.class-teacher',
        key: 'classTeacher',
        width: '20%',
        sorter: false,
        render: (row: ISubscriptionChildDetails) =>
          row?.class?.class_teacher?.name ? (
            <div className="flex items-center gap-2">
              <AvatarGroupList
                list={[
                  {
                    id: row?.class?.class_teacher?.id,
                    name: row?.class?.class_teacher?.name,
                    image: getAssetUrl(row?.class?.class_teacher?.asset),
                  },
                ]}
                size={40}
                avatarClassName="w-8 h-8 text-[10px]"
              />
              <span className="text-sm font-medium text-neutral-primary">
                {row?.class?.class_teacher?.name}
              </span>
            </div>
          ) : (
            '-'
          ),
      },
      {
        title: 'children.school-subscriber.columns.status',
        key: 'status',
        width: '15%',
        sorter: true,
        handleSort: () => handleSort('status'),
        render: (row: ISubscriptionChildDetails) => {
          const config =
            CommonStatusConfig[row?.status as keyof typeof CommonStatusConfig];
          return (
            <span
              className={clsx(
                'px-2 py-1 rounded-full text-xs font-semibold',
                config?.className
              )}
            >
              {formatText(config?.label)}
            </span>
          );
        },
      },
      {
        title: 'children.school-subscriber.columns.last-active',
        key: 'last_active_at',
        width: '20%',
        sorter: true,
        handleSort: () => handleSort('last_active_at'),
        render: (row: ISubscriptionChildDetails) =>
          formatLastActive(Number(row?.last_active_at), formatText),
      },
      {
        title: 'children.school-subscriber.columns.action',
        key: 'action',
        width: '5%',
        align: 'center',
        render: (row: ISubscriptionChildDetails) => {
          const isBlocked = row?.status === ECommonStatus.IN_ACTIVE;

          const actions = [
            {
              key: 'view',
              label: 'common.actions.view',
              icon: <IconEye size={18} />,
              onClick: () => {
                setSelectedChildren({
                  name: row?.name,
                  id: row?.id,
                  status: row?.status,
                });
                setIsChildDetailsModalOpen(true);
              },
            },
            {
              key: isBlocked ? 'unblock' : 'block',
              label: isBlocked
                ? 'common.actions.unblock'
                : 'common.actions.block',
              icon: isBlocked ? (
                <IconLockOpen size={18} />
              ) : (
                <IconXboxX size={18} />
              ),
              onClick: () => {
                if (isBlocked) {
                  handleBlockChildren(row);
                } else {
                  setSelectedChildren({
                    id: row?.id,
                    name: row?.name,
                    status: row?.status,
                  });
                  setIsOpenBlockModal(true);
                }
              },
            },
            {
              key: 'remove',
              label: 'common.actions.remove',
              icon: <IconTrash size={18} />,
              isDanger: true,
              onClick: () => {
                setSelectedChildren({
                  id: row?.id,
                  name: row?.name,
                  status: row?.status,
                });
                setIsRemoveChildrenModalOpen(true);
              },
            },
          ];

          return <ActionDropdown actions={actions} />;
        },
      },
    ];

    const isFilterApplied =
      !!filterValues?.status ||
      !!(
        Array.isArray(filterValues?.school_id) &&
        filterValues?.school_id?.length > 0
      ) ||
      !!(
        Array.isArray(filterValues?.class_id) &&
        filterValues?.class_id?.length > 0
      ) ||
      !!(
        Array.isArray(filterValues?.teacher_id) &&
        filterValues?.teacher_id?.length > 0
      );

    const showListUI =
      loadingTable ||
      childrens?.length > 0 ||
      search?.length > 0 ||
      debouncedSearch?.length > 0 ||
      isFilterApplied;

    return (
      <>
        {showListUI && (
          <>
            <div className="flex flex-wrap justify-between items-center gap-2.5 mb-6 pl-1">
              <div className="flex items-center gap-2.5">
                <Input
                  name="search"
                  placeholder={formatText(
                    'children.school-subscriber.search-placeholder'
                  )}
                  value={search}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSearch(e.target.value)
                  }
                  leftIcon={{
                    icon: <IconSearch size={20} className="text-neutral-500" />,
                  }}
                  className="min-w-70"
                  inputClassName="py-[13px]"
                />
                <div className="relative">
                  <Button
                    icon={<IconAdjustments size={20} />}
                    label={formatText('common.actions.filter')}
                    variant="outline"
                    className={clsx(
                      'px-4 h-12 cursor-pointer',
                      isFilterApplied &&
                        'border-brand-default! text-brand-default!'
                    )}
                    onClick={() => setIsFilterModalOpen(true)}
                  />
                  {isFilterApplied && (
                    <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-brand-default border-2 border-white" />
                  )}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-4">
              {statsData?.map((stat, index) => (
                <SummaryCard
                  key={index}
                  variant="dashboard"
                  label={formatText(stat.label)}
                  value={stat?.value}
                  icon={stat?.icon}
                  trendDirection={stat?.trendDirection}
                  isPositive={stat?.isPositive}
                  trendLabel={
                    stat?.trendLabel ? formatText(stat.trendLabel) : undefined
                  }
                  trend={stat?.trend}
                  isLoading={loadingSummary}
                />
              ))}
            </div>
          </>
        )}
        <div className="mt-6">
          <CustomTable
            isLoading={loadingTable}
            dataSource={childrens}
            columns={columns}
            total={total}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pageSize={pageSize}
            fieldName={sortField}
            sortDirection={sortDirection}
            rootClassName="h-[calc(100vh-670px)] lg:h-[calc(100vh-402px)]"
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            isEmpty={
              !loadingTable &&
              childrens?.length === 0 &&
              search?.length === 0 &&
              debouncedSearch?.length === 0 &&
              !isFilterApplied
            }
            emptyTitle={formatText(
              'children.school-subscriber.not-found-title'
            )}
            emptyDescription={formatText(
              'children.school-subscriber.not-found-desc'
            )}
            isEmptySearch={
              childrens?.length === 0 &&
              (search?.length > 0 ||
                debouncedSearch?.length > 0 ||
                isFilterApplied)
            }
            onRowClick={(id) => {
              const child = childrens.find((item) => item.id === id);
              if (child) {
                setSelectedChildren({
                  name: child?.name,
                  id: child?.id,
                  status: child?.status,
                });
                setIsChildDetailsModalOpen(true);
              }
            }}
          />
        </div>
        <Activity mode={isFilterModalOpen ? 'visible' : 'hidden'}>
          <ChildrenFilterModal
            setIsOpen={setIsFilterModalOpen}
            onApply={handleFilterApply}
            schoolOptions={schoolOptions}
            initialValues={filterValues}
          />
        </Activity>
      </>
    );
  }
);

// Action Dropdown Component (Internal to avoid new file)

export default SchoolSubscriber;
